import type { OfferDraftRowKind } from '../../../types';

export const MIN_TITLE_LEVEL = 1;
export const MAX_TITLE_LEVEL = 4;
export const TITLE_INDENT_STEP_PX = 16;
export const TITLE_INDENT_STEP_PT = 8;

export function clampTitleLevel(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return MIN_TITLE_LEVEL;
  return Math.max(MIN_TITLE_LEVEL, Math.min(MAX_TITLE_LEVEL, Math.floor(n)));
}

export function titleLevelFromOz(oz: string | null | undefined): number {
  const parts = String(oz ?? '')
    .split('.')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return MIN_TITLE_LEVEL;
  return clampTitleLevel(parts.length);
}

export function isTitlePosition(row: { source_type?: string | null; unit?: string | null }): boolean {
  return String(row.source_type ?? '').trim() === 'custom_title' || String(row.unit ?? '').trim() === 'Titel';
}

/** First title is always level 1; each next title may drop at most one level below the previous. */
export function nextTitleLevel(previousTitleLevel: number | null, requested?: unknown): number {
  const fallback = previousTitleLevel ?? MIN_TITLE_LEVEL;
  const want = requested == null || requested === '' ? fallback : clampTitleLevel(requested);
  if (previousTitleLevel == null) return MIN_TITLE_LEVEL;
  return Math.min(want, previousTitleLevel + 1, MAX_TITLE_LEVEL);
}

/** Clamp stored title levels to 1–4 and at most one step deeper than the previous title. */
export function normalizeTitleLevels<T extends { kind: OfferDraftRowKind; titleLevel?: number | null }>(
  rows: T[],
): T[] {
  let previous: number | null = null;
  return rows.map((row) => {
    if (row.kind !== 'title') return row;
    const titleLevel = nextTitleLevel(previous, row.titleLevel);
    previous = titleLevel;
    return { ...row, titleLevel };
  });
}

export function previousTitleLevelAt<T extends { kind: OfferDraftRowKind; titleLevel?: number | null }>(
  rows: T[],
  index: number,
): number | null {
  for (let i = index - 1; i >= 0; i -= 1) {
    const row = rows[i];
    if (row?.kind === 'title') return clampTitleLevel(row.titleLevel ?? MIN_TITLE_LEVEL);
  }
  return null;
}

export function maxAllowedTitleLevelAt<T extends { kind: OfferDraftRowKind; titleLevel?: number | null }>(
  rows: T[],
  index: number,
): number {
  const previous = previousTitleLevelAt(rows, index);
  if (previous == null) return MIN_TITLE_LEVEL;
  return Math.min(previous + 1, MAX_TITLE_LEVEL);
}

/**
 * Rows that belong to a title's section: everything after the title up to (excluding) the next
 * title at the same or a higher level. Nested titles and their rows are included. Empty when the
 * id is not a title.
 */
export function titleSectionRows<T extends { id: string; kind: OfferDraftRowKind; titleLevel?: number | null }>(
  rows: T[],
  titleId: string,
): T[] {
  const normalized = normalizeTitleLevels(rows);
  const start = normalized.findIndex((row) => row.id === titleId);
  if (start < 0 || normalized[start].kind !== 'title') return [];
  const level = clampTitleLevel(normalized[start].titleLevel);
  const section: T[] = [];
  for (let i = start + 1; i < normalized.length; i += 1) {
    const row = normalized[i];
    if (row.kind === 'title' && clampTitleLevel(row.titleLevel) <= level) break;
    section.push(rows[i]);
  }
  return section;
}

export function titleIndentLevel(rowDepth: number | null | undefined): number {
  const depth = Number(rowDepth) || 0;
  return Math.max(0, depth - 1);
}

export function titleIndentPx(rowDepth: number | null | undefined, stepPx = TITLE_INDENT_STEP_PX): number {
  return titleIndentLevel(rowDepth) * stepPx;
}

export function titleIndentPt(rowDepth: number | null | undefined, stepPt = TITLE_INDENT_STEP_PT): number {
  return titleIndentLevel(rowDepth) * stepPt;
}

export type HierarchicalNumberedRow<T> = T & {
  numberLabel: string;
  rowDepth: number;
  titleLevel: number;
};

/**
 * Walk a flat row list and assign nested OZ labels (1 / 1.1 / 1.1.1).
 * Title levels are normalized in place on the returned objects.
 */
export function assignHierarchicalNumberLabels<
  T extends { kind: OfferDraftRowKind; titleLevel?: number | null },
>(rows: T[]): Array<HierarchicalNumberedRow<T>> {
  const counters: number[] = [];
  let child = 0;
  let root = 0;

  return rows.map((row) => {
    if (row.kind === 'title') {
      const previous = counters.length === 0 ? null : counters.length;
      const level = nextTitleLevel(previous, row.titleLevel);
      while (counters.length > level) counters.pop();
      if (counters.length === 0 && level === MIN_TITLE_LEVEL && root > 0) {
        counters.push(root + 1);
      } else if (counters.length === level) {
        counters[level - 1] += 1;
      } else {
        counters.push(1);
      }
      child = 0;
      const numberLabel = counters.join('.');
      return { ...row, numberLabel, rowDepth: level, titleLevel: level };
    }

    if (counters.length === 0) {
      root += 1;
      return { ...row, numberLabel: `${root}`, rowDepth: 0, titleLevel: 0 };
    }

    child += 1;
    const parentLevel = counters.length;
    return {
      ...row,
      numberLabel: `${counters.join('.')}.${child}`,
      rowDepth: parentLevel,
      titleLevel: parentLevel,
    };
  });
}
